// PreviewTester/components/PreviewContainer.js
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./PreviewContainer.module.css";

const PreviewContainer = forwardRef(
  (
    {
      getScrollStyle,
      getPreviewContent,
      viewMode,
      onTextSelect,
      isSelectingMode,
      // previewViewMode = 'desktop',
      currentPhase = 0,
      showTransformationPanel = false,
    },
    ref,
  ) => {
    const renderCountRef = useRef(0);
    const containerRef = useRef(null);
    const selectionTimeoutRef = useRef(null);

    // Forward the ref to parent
    useImperativeHandle(ref, () => containerRef.current);

    // Log when component renders
    renderCountRef.current += 1;
    console.log(
      `[PreviewContainer] 🔄 Render #${renderCountRef.current}, viewMode: "${viewMode}"`,
    );
    console.log(`[PreviewContainer] 📊 isSelectingMode: ${isSelectingMode}`);
    // console.log(`[PreviewContainer] 📊 previewViewMode: ${previewViewMode}`);
    console.log(`[PreviewContainer] 📊 currentPhase: ${currentPhase}`);
    console.log(
      `[PreviewContainer] 📊 showTransformationPanel: ${showTransformationPanel}`,
    );

    // Helper: Check if element is inside structural content (Card, Table, List, JSON)
    const isInsideStructuralContent = (element) => {
      if (!element) return false;

      // Rows are the atomic styleable unit. If the selection is inside a
      // .row that is *not* inside a table/list, it's styleable.
      const row = element.closest?.(".row");
      if (row) {
        // The row itself must not be inside a table/list cell
        if (row.closest(".previewTable, .previewList")) return true;
        return false; // plain row (with or without a card wrapper) → styleable
      }

      // Not inside a row → fall back to legacy structural detection
      const structuralClasses = [
        "previewTable",
        "previewList",
        "previewCardTitle",
      ];
      let current = element;
      while (current && current !== document.body) {
        if (current.classList) {
          for (const cls of structuralClasses) {
            if (current.classList.contains(cls)) return true;
          }
        }
        current = current.parentElement;
      }
      return false;
    };

    // Helper: Check if selection is on an image-url element inside a card
    const isImageUrlInCard = (element) => {
      if (!element) return false;

      let current = element;
      while (current && current !== document.body) {
        if (current.classList && current.classList.contains("image-url")) {
          let parent = current.parentElement;
          while (parent && parent !== document.body) {
            if (parent.classList && parent.classList.contains("previewCard")) {
              return true;
            }
            parent = parent.parentElement;
          }
        }
        current = current.parentElement;
      }
      return false;
    };

    // Apply phase-specific visual indicators
    const applyPhaseIndicators = () => {
      const screens = document.querySelectorAll(".item-ui-screen");
      const firstScreen = screens[0];

      if (!firstScreen) return;

      // Remove all existing indicator classes from ALL loops first
      const allLoops = document.querySelectorAll(".for-loop-started");
      allLoops.forEach((loop) => {
        loop.classList.remove(
          "loop-phase-active",
          "loop-phase-media",
          "loop-phase-style",
        );
        loop.style.background = "";
        loop.style.border = "";
        loop.style.padding = "";
        loop.style.margin = "";
        loop.style.borderRadius = "";
        loop.style.position = "";
        loop.style.boxShadow = "";
        loop.style.backgroundColor = "";
      });

      // Remove dynamic style tag AND any leftover ring overlays
      const styleTag = document.getElementById("phase-indicator-styles");
      if (styleTag) styleTag.remove();
      document.querySelectorAll(".loop-phase-ring").forEach((r) => r.remove());

      // Only apply indicators during Structural phase (phase 0) when in selecting mode
      if (isSelectingMode && currentPhase === 0) {
        const loops = firstScreen.querySelectorAll(".for-loop-started");
        loops.forEach((loop) => {
          // Skip loops that already contain structural transformations
          const hasStructural = loop.querySelector(
            ".previewCard, .previewTable, .previewList",
          );
          if (hasStructural) return;

          loop.classList.add("loop-phase-active");

          // ------------------------------------------------------------
          // Attach a properly-drawn ring overlay using SVG.
          // SVG stroke + stroke-dasharray gives us an animated dashed
          // ring that renders identically in every browser and never
          // pokes outside the container.
          // ------------------------------------------------------------
          if (!loop.querySelector(":scope > .loop-phase-ring")) {
            const ring = document.createElement("div");
            ring.className = "loop-phase-ring";

            ring.innerHTML = `
            <svg width="100%" height="100%" viewBox="0 0 100 100"
                 preserveAspectRatio="none"
                 xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="loopRingGradient" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%"  stop-color="#f6ad55" />
                  <stop offset="50%" stop-color="#ed8936" />
                  <stop offset="100%" stop-color="#dd6b20" />
                </linearGradient>
              </defs>
              <rect
                x="1" y="1"
                width="98" height="98"
                rx="0" ry="0"
                fill="none"
                stroke="url(#loopRingGradient)"
                stroke-width="2"
                vector-effect="non-scaling-stroke"
                stroke-dasharray="14 8"
                stroke-linecap="round"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="-44"
                  dur="2.4s"
                  repeatCount="indefinite"
                />
              </rect>
            </svg>
          `;

            ring.style.cssText = `
            position: absolute;
            inset: -3px;
            pointer-events: none;
            z-index: 4;
            border-radius: 14px;
            opacity: 0;
            transition: opacity 0.4s ease;
          `;

            // Ensure the loop is a positioning context
            if (getComputedStyle(loop).position === "static") {
              loop.style.position = "relative";
            }
            loop.appendChild(ring);

            // Fade the ring in
            requestAnimationFrame(() => {
              ring.style.opacity = "1";
            });
          }
        });

        // ------------------------------------------------------------
        // Inject the stylesheet (breathing bg + badges, no conic mask)
        // ------------------------------------------------------------
        const style = document.createElement("style");
        style.id = "phase-indicator-styles";
        style.textContent = `
        .for-loop-started.loop-phase-active {
          position: relative;
          border-radius: 14px;
          padding: 10px 14px;
          margin: 8px 0;
          background: linear-gradient(
            135deg,
            rgba(255, 247, 237, 0.75),
            rgba(255, 237, 213, 0.55)
          );
          transition:
            background 0.35s ease,
            box-shadow 0.35s ease,
            transform 0.35s ease;
          animation: loopBreathe 3.6s ease-in-out infinite;
          background: transparent
        }

        .for-loop-started.loop-phase-active:hover {
          background: linear-gradient(
            135deg,
            rgba(255, 244, 230, 0.95),
            rgba(255, 227, 190, 0.75)
          );
          transform: translateY(-1px);
          // box-shadow: 0 8px 28px rgba(237, 137, 54, 0.22);
          cursor: pointer;
          background: transparent;
        }

        .for-loop-started.loop-phase-active:hover .loop-phase-ring {
          filter: drop-shadow(0 0 6px rgba(237, 137, 54, 0.55));
        }

        .loop-phase-ring svg {
          display: block;
          width: 100%;
          height: 100%;
          overflow: visible;
        }

        .loop-phase-badge {
          position: absolute;
          top: -12px;
          left: 14px;
          background: linear-gradient(135deg, #f6ad55, #dd6b20);
          color: white;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.3px;
          width: fit-content;
          max-width: max-content;
          padding: 3px 10px;
          border-radius: 12px;
          box-shadow: 0 4px 10px rgba(221, 107, 32, 0.35);
          z-index: 5;
          pointer-events: none;
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          animation: loopBadgeFloat 3.6s ease-in-out infinite;
        }

        @keyframes loopBreathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.003); }
        }

        @keyframes loopBadgeFloat {
          0%, 100% { transform: translateY(0);   opacity: 1; }
          50%      { transform: translateY(-2px); opacity: 0.9; }
        }

        /* ============================================================
           ✅ Screen-phase badge: "Work on this screen"
           Rendered as a sibling of the screens container, absolutely
           positioned above the first screen. Does not enter any
           .item-ui-screen's subtree.
           ============================================================ */
        .screen-phase-badge {
          position: absolute;
          top: -12px;
          left: 14px;
          background: linear-gradient(135deg, #4a7feb, #3b6bd4);
          color: white;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.3px;
          padding: 3px 10px;
          border-radius: 12px;
          box-shadow: 0 4px 10px rgba(74, 127, 235, 0.35);
          z-index: 6;
          pointer-events: none;
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          animation: screenBadgeFloat 3.6s ease-in-out infinite;
        }

        @keyframes screenBadgeFloat {
          0%, 100% { transform: translateY(0);   opacity: 1; }
          50%      { transform: translateY(-2px); opacity: 0.9; }
        }
      `;
        document.head.appendChild(style);

        // ------------------------------------------------------------
        // Add the small "Select this group" badge above each ring
        // ------------------------------------------------------------
        loops.forEach((loop) => {
          const hasStructural = loop.querySelector(
            ".previewCard, .previewTable, .previewList",
          );
          if (hasStructural) return;

          if (!loop.querySelector(":scope > .loop-phase-badge")) {
            const badge = document.createElement("div");
            badge.className = "loop-phase-badge";
            badge.textContent = "👉 Select this group";
            loop.appendChild(badge);
          }
        });

        // ------------------------------------------------------------
        // ✅ "Work on this screen" badge for multi-screen layouts.
        //
        // Anchored to the screens CONTAINER (.verticals / .horizontals),
        // NOT to the screen itself — so no DOM is mutated inside any
        // .item-ui-screen. This keeps the screen's rendered content
        // untouched.
        //
        // Only rendered when there are 2+ screens, since the badge's
        // whole purpose is to tell the user "you can only transform
        // the first one".
        // ------------------------------------------------------------
        const screenCount = screens.length;
        if (screenCount > 1) {
          // Prefer the container that wraps all screens; fall back to
          // the parent of the first screen.
          const screensContainer =
            document.querySelector(".verticals, .horizontals") ||
            firstScreen.parentElement;

          if (screensContainer) {
            // Ensure the container is a positioning context for the badge.
            if (getComputedStyle(screensContainer).position === "static") {
              screensContainer.style.position = "relative";
            }

            let screenBadge = screensContainer.querySelector(
              ":scope > .screen-phase-badge",
            );

            if (!screenBadge) {
              screenBadge = document.createElement("div");
              screenBadge.className = "screen-phase-badge";
              screenBadge.textContent = "✏️ Work on this screen";
              screensContainer.appendChild(screenBadge);
            }
          }
        }
      }
    };

    // Apply phase indicators when selecting mode or phase changes
    useEffect(() => {
      const timer = setTimeout(() => {
        applyPhaseIndicators();
      }, 50);

      return () => clearTimeout(timer);
    }, [isSelectingMode, currentPhase]);

    // Clean up visual styles when wizard mode is deactivated OR phase changes
    useEffect(() => {
      const cleanupIndicators = () => {
        const allLoops = document.querySelectorAll(".for-loop-started");
        allLoops.forEach((loop) => {
          loop.classList.remove(
            "loop-phase-active",
            "loop-phase-media",
            "loop-phase-style",
          );
          loop.style.background = "";
          loop.style.border = "";
          loop.style.padding = "";
          loop.style.margin = "";
          loop.style.borderRadius = "";
          loop.style.position = "";
          loop.style.boxShadow = "";
          loop.style.backgroundColor = "";
        });

        // Remove SVG ring overlays + badges
        document
          .querySelectorAll(".loop-phase-ring")
          .forEach((r) => r.remove());
        document
          .querySelectorAll(".loop-phase-badge")
          .forEach((b) => b.remove());
        // ✅ Remove the screen-level badge too
        document
          .querySelectorAll(".screen-phase-badge")
          .forEach((b) => b.remove());

        const styleTag = document.getElementById("phase-indicator-styles");
        if (styleTag) {
          styleTag.remove();
        }
      };

      if (!isSelectingMode) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
          selectionTimeoutRef.current = null;
        }

        const screens = document.querySelectorAll(".item-ui-screen");
        screens.forEach((screen) => {
          screen.style.border = "";
          screen.style.borderRadius = "";
          screen.style.boxShadow = "";
          screen.style.opacity = "";
          screen.style.userSelect = "";
          screen.style.pointerEvents = "";
          screen.style.cursor = "";
        });

        cleanupIndicators();
      } else {
        // If we're in selecting mode but NOT in structural phase, clean up indicators
        if (currentPhase !== 0) {
          cleanupIndicators();
        }
      }
    }, [isSelectingMode, currentPhase]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
        }
        const styleTag = document.getElementById("phase-indicator-styles");
        if (styleTag) {
          styleTag.remove();
        }
        // Remove ring overlays + badges on unmount
        document
          .querySelectorAll(".loop-phase-ring")
          .forEach((r) => r.remove());
        document
          .querySelectorAll(".loop-phase-badge")
          .forEach((b) => b.remove());
        // ✅ Remove the screen-level badge on unmount
        document
          .querySelectorAll(".screen-phase-badge")
          .forEach((b) => b.remove());
      };
    }, []);

    // Log when viewMode changes
    useEffect(() => {
      console.log(`[PreviewContainer] 📊 viewMode changed to: "${viewMode}"`);
    }, [viewMode]);

    // Log when getPreviewContent is called
    const handleGetPreviewContent = () => {
      console.log(
        `[PreviewContainer] 📝 Calling getPreviewContent with viewMode: "${viewMode}"`,
      );
      const result = getPreviewContent();
      console.log(`[PreviewContainer] 📝 getPreviewContent completed`);
      return result;
    };

    // Handle text selection - phase-aware structural check
    const handleSelection = () => {
      if (!isSelectingMode) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const selectedText = selection.toString();

      if (selectedText && selectedText.trim().length > 0) {
        let container = range.startContainer;
        let screen = container.closest?.(".item-ui-screen");

        if (!screen) {
          const screens = document.querySelectorAll(".item-ui-screen");
          for (const s of screens) {
            if (s.contains(container)) {
              screen = s;
              break;
            }
          }
        }

        if (screen) {
          const screenIndex = screen.getAttribute("data-item-ui-index") || "0";
          const isFirstScreen = screenIndex === "0";

          // Only allow selection on the first screen
          if (!isFirstScreen) {
            console.log(
              "[PreviewContainer] 🚫 Selection on screen",
              screenIndex,
              "is not allowed - please select on first screen",
            );
            selection.removeAllRanges();
            return;
          }

          // Phase-aware structural content check
          const isStructural =
            isInsideStructuralContent(range.startContainer) ||
            isInsideStructuralContent(range.endContainer);

          if (isStructural) {
            // During Media phase (phase 1): Allow selection ONLY on image-url elements inside cards
            if (currentPhase === 1) {
              const isImageUrl =
                isImageUrlInCard(range.startContainer) ||
                isImageUrlInCard(range.endContainer);

              if (isImageUrl) {
                console.log(
                  "[PreviewContainer] ✅ Media phase: Selection on image-url inside card is allowed",
                );
                if (onTextSelect) {
                  onTextSelect(selectedText, range, screen);
                }
                return;
              } else {
                console.log(
                  "[PreviewContainer] 🚫 Media phase: Selection on non-image content inside card is not allowed",
                );
                selection.removeAllRanges();
                return;
              }
            }

            // For all other phases: Block selection on structural content
            console.log(
              "[PreviewContainer] 🚫 Selection inside structural content is not allowed in this phase",
            );
            selection.removeAllRanges();
            return;
          }

          // Valid selection on first screen (non-structural content)
          if (onTextSelect) {
            onTextSelect(selectedText, range, screen);
          }
        }
      } else {
        if (onTextSelect) {
          onTextSelect("", null, null);
        }
      }
    };

    return (
      <div
        ref={containerRef}
        key={`preview-${viewMode}`}
        className={`${styles.previewContainer} ${isSelectingMode ? styles.selectable : ""}`}
        style={{
          ...getScrollStyle(),
          cursor: isSelectingMode ? "text" : "default",
          userSelect: isSelectingMode ? "text" : "auto",
          transition: "max-width 0.3s ease, margin 0.3s ease",
        }}
        onMouseUp={handleSelection}
        onTouchEnd={handleSelection}
        onMouseLeave={() => {
          if (isSelectingMode) {
            const selection = window.getSelection();
            if (selection) {
              // Don't clear immediately, let the user finish selecting
            }
          }
        }}
      >
        {handleGetPreviewContent()}
      </div>
    );
  },
);

PreviewContainer.displayName = "PreviewContainer";

export default PreviewContainer;
